'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { ChevronRight, Loader2, Check, X, Terminal, Globe, File, Wrench } from 'lucide-react'

interface ToolProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultOpen?: boolean
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

const ToolContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
} | null>(null)

export function Tool({
  children,
  defaultOpen = false,
  isOpen: controlledOpen,
  onOpenChange,
  className,
  ...props
}: ToolProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(value)
      }
      onOpenChange?.(value)
    },
    [isControlled, onOpenChange]
  )

  return (
    <ToolContext.Provider value={{ open, setOpen }}>
      <div
        className={cn('group w-full rounded-lg border bg-card text-card-foreground shadow-sm', className)}
        {...props}
      >
        {children}
      </div>
    </ToolContext.Provider>
  )
}

interface ToolHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  tool: string
  state: 'pending' | 'success' | 'error'
}

export function ToolHeader({ tool, state, className, ...props }: ToolHeaderProps) {
  const context = React.useContext(ToolContext)
  if (!context) throw new Error('ToolHeader must be used within Tool')
  const { open, setOpen } = context

  const getIcon = () => {
    switch (state) {
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      case 'success':
        return <Check className="h-4 w-4 text-green-500" />
      case 'error':
        return <X className="h-4 w-4 text-red-500" />
    }
  }

  const getToolIcon = (name: string) => {
    if (name.includes('web') || name.includes('http') || name.includes('fetch')) return Globe
    if (name.includes('file') || name.includes('read') || name.includes('write')) return File
    if (name.includes('cmd') || name.includes('exec') || name.includes('terminal')) return Terminal
    return Wrench
  }

  const ToolIcon = getToolIcon(tool)

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 cursor-pointer select-none hover:bg-muted/50 transition-colors',
        className
      )}
      onClick={() => setOpen(!open)}
      {...props}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {state === 'pending' ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className={cn(
            "p-1 rounded-md",
            state === 'error' ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" : "bg-muted text-muted-foreground"
          )}>
            <ToolIcon className="h-3.5 w-3.5" />
          </div>
        )}
        <span className="text-sm font-medium truncate">{tool}</span>
      </div>
      
      <ChevronRight
        className={cn(
          'h-4 w-4 text-muted-foreground transition-transform duration-200',
          open && 'rotate-90'
        )}
      />
    </div>
  )
}

export function ToolContent({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const context = React.useContext(ToolContext)
  if (!context) throw new Error('ToolContent must be used within Tool')
  const { open } = context

  if (!open) return null

  return (
    <div className={cn('px-4 pb-3 pt-0 border-t bg-muted/30', className)} {...props}>
      <div className="space-y-3 pt-3">
        {children}
      </div>
    </div>
  )
}

interface ToolInputProps {
  input: Record<string, unknown>
  className?: string
}

export function ToolInput({ input, className }: ToolInputProps) {
  const isEmpty = Object.keys(input).length === 0
  
  return (
    <div className={cn('space-y-1.5 w-full max-w-full', className)}>
      <span className="text-xs font-medium text-muted-foreground">Input</span>
      <div className="rounded-md border bg-background px-3 py-2 w-full max-w-full overflow-hidden">
        <pre className="text-xs overflow-auto max-h-[200px] whitespace-pre-wrap break-words font-mono">
          {isEmpty ? '{}' : JSON.stringify(input, null, 2)}
        </pre>
      </div>
    </div>
  )
}

interface ToolOutputProps {
  output?: unknown
  error?: string
  className?: string
}

function isShellOutput(output: unknown): output is { stdout: string; stderr: string; exitCode: number } {
  return typeof output === 'object' && output !== null && 'stdout' in output && 'exitCode' in output;
}

interface StructuredSearchResult {
  facts: string[];
  date_info?: string;
  confidence: 'high' | 'medium' | 'low';
}

interface SubAgentOutput {
  content: string;
  sources?: Array<{ uri: string; title: string }>;
  searchQueries?: string[];
  structured?: StructuredSearchResult;
  usage?: { model: string; inputTokens: number; outputTokens: number; cost: number };
}

function isSubAgentOutput(output: unknown): output is SubAgentOutput {
  return typeof output === 'object' && output !== null && 'content' in output && typeof (output as SubAgentOutput).content === 'string';
}

export function ToolOutput({ output, error, className }: ToolOutputProps) {
  if (output === undefined && !error) return null

  return (
    <div className={cn('space-y-1.5 w-full max-w-full', className)}>
      <span className={cn(
        "text-xs font-medium",
        error ? "text-red-500" : "text-muted-foreground"
      )}>
        {error ? 'Error' : 'Output'}
      </span>
      <div className={cn(
        "rounded-md border px-3 py-2 w-full max-w-full overflow-hidden",
        error ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-900" : "bg-background"
      )}>
        {error ? (
          <pre className="text-xs text-red-600 dark:text-red-400 overflow-auto whitespace-pre-wrap break-words font-mono">
            {error}
          </pre>
        ) : isShellOutput(output) ? (
          <div className="flex flex-col gap-2">
            {output.stdout && (
              <div>
                <div className="text-[10px] uppercase text-muted-foreground font-semibold mb-1">stdout</div>
                <div className="bg-muted/50 p-2 rounded text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-auto">{output.stdout}</div>
              </div>
            )}
            {output.stderr && (
              <div>
                <div className="text-[10px] uppercase text-destructive/80 font-semibold mb-1">stderr</div>
                <div className="bg-destructive/10 text-destructive p-2 rounded text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-auto">{output.stderr}</div>
              </div>
            )}
            <div className="text-[10px] text-muted-foreground font-mono">Exit Code: {output.exitCode}</div>
          </div>
        ) : isSubAgentOutput(output) ? (
          <div className="flex flex-col gap-3">
            {/* Structured JSON (if available) - includes sources from grounding */}
            {output.structured && (
              <details className="group/json">
                <summary className="text-[10px] uppercase text-emerald-600 dark:text-emerald-400 font-semibold cursor-pointer hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center gap-1">
                  <svg className="w-3 h-3 transition-transform group-open/json:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Raw JSON
                  <span className="ml-1 text-muted-foreground font-normal">
                    ({output.structured.confidence} confidence)
                  </span>
                </summary>
                <pre className="mt-1 bg-muted/50 p-2 rounded text-[11px] font-mono whitespace-pre-wrap overflow-auto max-h-[200px]">
                  {JSON.stringify({
                    ...output.structured,
                    sources: output.sources?.map(s => s.title || s.uri),
                    searchQueries: output.searchQueries,
                  }, null, 2)}
                </pre>
              </details>
            )}
            
            {/* Content */}
            <div className="text-xs whitespace-pre-wrap break-words">
              {output.content}
            </div>
            
            {/* Sources */}
            {output.sources && output.sources.length > 0 && (
              <div className="border-t pt-2">
                <div className="text-[10px] uppercase text-blue-600 dark:text-blue-400 font-semibold mb-1 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Sources ({output.sources.length})
                </div>
                <ul className="space-y-0.5">
                  {output.sources.slice(0, 5).map((source, i) => (
                    <li key={i} className="text-[11px]">
                      <a 
                        href={source.uri} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline truncate block"
                        title={source.uri}
                      >
                        {source.title || new URL(source.uri).hostname}
                      </a>
                    </li>
                  ))}
                  {output.sources.length > 5 && (
                    <li className="text-[10px] text-muted-foreground">
                      +{output.sources.length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            )}
            
            {/* Usage */}
            {output.usage && (
              <div className="text-[10px] text-muted-foreground font-mono border-t pt-1">
                {output.usage.model} · {output.usage.inputTokens}→{output.usage.outputTokens} tokens · ${output.usage.cost.toFixed(4)}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs overflow-auto max-h-[300px] whitespace-pre-wrap break-words font-mono">
             {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
          </div>
        )}
      </div>
    </div>
  )
}

